<template>
  <f7-page name="groups-list" class="groups-page" ptr @ptr:refresh="refresh">
    <f7-navbar :title="$t('groups.title')" back-link />
    <AppLayout>
      <template #main>
        <!-- Header with count -->
        <f7-block
          v-if="!loading && totalCount > 0"
          class="groups-header "
        >
          <div
            class="display-flex align-items-center justify-content-space-between"
          >
            <span class="font-headline-sm text-on-surface font-bold">{{
              $t("groups.title")
            }}</span>
            <span class="groups-count-badge">{{
              $t("groups.subtitle", { count: totalCount })
            }}</span>
          </div>
        </f7-block>

        <!-- Loading state -->
        <SkeletonList v-if="loading" variant="row" :rows="4" />

        <!-- Empty state -->
        <f7-block v-else-if="groupsList.length === 0" class="empty-state">
          <CtaMessageCard
            variant="catalyst"
            icon="person_3_fill"
            :title="$t('groups.emptyTitle')"
            :description="$t('groups.emptyDesc')"
            :button-label="$t('groups.createGroup')"
            @action="goToCreate"
          />
        </f7-block>

        <!-- Groups list -->
        <f7-list v-else media-list inset  >
          <f7-list-group>
            <f7-card v-for="group in groupsList" :key="group.id" strong class="group-card">
          <f7-list-item
           
            link="#"
            no-chevron
            @click="openGroup(group)"
            class="group-item"
            :class="{ 'group-item--draft': group.status === 'DRAFT' }"
          >
            <!-- Logo or initials -->
            <template #media>
              <img
                v-if="group.logoUrl"
                :src="group.logoUrl"
                class="group-logo"
                :alt="group.name"
              />
              <f7-badge v-else  class="group-logo">
                {{ initials(group.name) }}
              </f7-badge>
            </template>

            <!-- Name + badges -->
            <template #title>
              <div class="display-flex align-items-center gap-1 flex-wrap">
                <span class="group-name">{{ group.name }}</span>
                <f7-badge
                  class="status-badge"
                  :class="'status-' + (group.status || 'ACTIVE').toLowerCase()"
                >
                  {{ statusLabel(group.status) }}
                </f7-badge>
              </div>
            </template>

            <!-- Category + visibility -->
            <template #subtitle>
              <div class="group-subtitle">
                <span class="category-text">{{
                  categoryLabel(group.category)
                }}</span>
                <span v-if="group.city" class="subtitle-dot">·</span>
                <span v-if="group.city" class="city-text">{{
                  group.city
                }}</span>
              </div>
            </template>

            <!-- Description + members -->
            <template #text>
              <p v-if="group.description" class="group-description">
                {{ truncate(group.description) }}
              </p>
              <div
                v-if="group.status === 'DRAFT'"
                class="draft-hint display-flex align-items-center gap-1"
              >
                <f7-icon material="info" :size="14" />
                {{ $t("groups.draftHint") }}
              </div>
            </template>

            <!-- After: member count -->
            <template #after>
              <span class="member-count display-flex align-items-center gap-1">
                <f7-icon material="group" :size="16" class="text-primary" />
                {{ group.memberCount ?? 0 }}
              </span>
            </template>
          </f7-list-item>
          </f7-card>
          </f7-list-group>
        </f7-list>

        <!-- Load more (paged backend) -->
        <f7-block v-if="!loading && hasMore" class="load-more-block">
          <f7-button outline round raised :disabled="loadingMore" @click="loadMore">
            <span
              v-if="loadingMore"
              class="display-flex align-items-center justify-content-center"
            >
              <f7-preloader size="16" />
            </span>
            <span v-else>{{ $t("groups.loadMore") }}</span>
          </f7-button>
        </f7-block>

        <!-- Create FAB -->
      </template>
    </AppLayout>
    <f7-fab
      v-if="!loading"
      position="center-bottom"
      :text="$t('groups.createGroup')"
      class="groups-fab"
      @click="goToCreate"
    >
      <f7-icon ios="f7:plus" md="material:add"></f7-icon>
    </f7-fab>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import SkeletonList from "../components/common/SkeletonList.vue";
import CtaMessageCard from "../components/home/CtaMessageCard.vue";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import i18n from "../js/i18n.js";

const { t } = i18n.global;
const props = defineProps({ f7route: Object, f7router: Object });

const groupsStore = useGroupsStore();
const loading = ref(true);
const loadingMore = ref(false);

// The store unwraps the Spring pageable, so `groups` is the content array.
const groupsList = computed(() => groupsStore.groups || []);
// totalElements from the page envelope (accurate across pages)
const totalCount = computed(
  () => groupsStore.groupsPage?.total ?? groupsList.value.length,
);
// `last === false` means the backend has more pages
const hasMore = computed(() => groupsStore.groupsPage?.last === false);

function initials(name) {
  if (!name) return "G";
  return name.trim().slice(0, 2).toUpperCase();
}

function truncate(text, length = 90) {
  if (!text) return "";
  return text.length > length ? text.slice(0, length).trim() + "…" : text;
}

function statusLabel(status) {
  const key = `groups.status.${status || "ACTIVE"}`;
  const label = t(key);
  return label !== key ? label : status || "ACTIVE";
}

function categoryLabel(category) {
  if (!category) return "";
  const key = `groups.category.${category}`;
  const label = t(key);
  return label !== key ? label : category;
}

function openGroup(group) {
  f7.views.main.router.navigate(`/groups/${group.id}`);
}

function goToCreate() {
  f7.views.main.router.navigate("/group/new");
}

async function load() {
  loading.value = true;
  try {
    await groupsStore.fetchGroups();
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || e?.message || t("common.loadError"),
    );
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  if (loadingMore.value || !hasMore.value) return;
  loadingMore.value = true;
  try {
    await groupsStore.loadMoreGroups();
  } catch (e) {
    f7.toast.show({
      text: e?.response?.data?.message || e?.message || t("common.loadError"),
      closeTimeout: 2000,
    });
  } finally {
    loadingMore.value = false;
  }
}

async function refresh(done) {
  try {
    await groupsStore.fetchGroups();
  } catch (e) {
    // silent on pull refresh
  } finally {
    done();
  }
}

onMounted(load);
</script>

<style scoped>
.groups-header {
  margin-bottom: 8px;
}
.groups-count-badge {
  font-size: 11px;
  font-weight: 600;
  color: var(--f7-on-surface-variant, #6f7a71);
  background: var(--f7-list-bg-color, #e8f0e7);
  padding: 3px 10px;
  border-radius: 9999px;
}
.groups-list {
  margin-top: 8px;
}
.load-more-block {
  margin: 8px 16px 16px;
}
.group-item {
  --f7-list-item-media-margin: 14px;
}
.group-item--draft {
  background: color-mix(in srgb, #a08520 4%, transparent);
}
.group-logo {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  object-fit: cover;
  display: block;
}
.group-logo--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--f7-theme-color, #1a6b42);
  color: #fff;
  font-family: var(--avocash-font-headline, "Manrope", sans-serif);
  font-size: 15px;
  font-weight: 700;
}
.group-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--f7-text-color, #161d18);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 55vw;
}
.status-badge {
  text-transform: uppercase;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.03em;
}
.status-badge.status-active {
  background: color-mix(in srgb, #1a6b42 12%, transparent);
  color: #1a6b42;
}
.status-badge.status-draft {
  background: color-mix(in srgb, #8e8e93 15%, transparent);
  color: #6f7a71;
}
.status-badge.status-archived {
  background: color-mix(in srgb, #8e8e93 12%, transparent);
  color: var(--f7-on-surface-variant, #8e8e93);
}
.status-badge.status-suspended {
  background: color-mix(in srgb, #772e37 12%, transparent);
  color: #772e37;
}
.group-subtitle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}
.category-text {
  color: var(--f7-theme-color, #1a6b42);
  font-weight: 600;
}
.subtitle-dot {
  color: var(--f7-on-surface-variant, #8e8e93);
}
.city-text {
  color: var(--f7-on-surface-variant, #6f7a71);
}
.group-description {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #6f7a71);
  line-height: 1.4;
  margin: 2px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.draft-hint {
  font-size: 11px;
  color: #a08520;
  margin-top: 4px;
}
.member-count {
  font-size: 12px;
  font-weight: 600;
  color: var(--f7-on-surface-variant, #6f7a71);
  white-space: nowrap;
}
.groups-fab {
  --f7-fab-text-color: #fff;
}
.empty-state {
  padding-top: 40px;
}
</style>
